import { useEffect, useState } from 'react'
import { Download } from 'lucide-react'
import invoiceApi from '@/services/invoiceApi'
import { Alert, Spinner } from '@/components/ds2'
import { InlineDocument, useInlineFile } from './InlineDocument'

interface DraftPreviewProps {
    invoiceId: string
    invoiceNumber: string | null
}

/**
 * The invoice draft as its real PDF: the same file the Invoices page shows (`invoiceApi.resolveInvoicePdf`, generated and cached on
 * first use), drawn inline from its bytes. Download saves that file; nothing is printed or sent from here.
 */
export const DraftPreview = ({ invoiceId, invoiceNumber }: DraftPreviewProps) => {
    const [pdfUrl, setPdfUrl] = useState<string | null>(null)
    const [failure, setFailure] = useState<string | null>(null)
    const title = `Invoice ${invoiceNumber ?? ''}`.trim()
    // The stored link is a storage download (served as an attachment): read as a typed blob, the PDF renders in the drawer.
    const file = useInlineFile(pdfUrl, title, 'application/pdf')

    useEffect(() => {
        let live = true
        setPdfUrl(null)
        setFailure(null)
        invoiceApi
            .resolveInvoicePdf(invoiceId)
            .then((result) => {
                if (!live) return
                if (result.ok) setPdfUrl(result.pdfUrl)
                else setFailure(result.message)
            })
            .catch(() => {
                if (live) setFailure('The draft PDF could not be loaded.')
            })
        return () => {
            live = false
        }
    }, [invoiceId])

    if (failure) return <Alert variant="danger" title={failure} />
    if (!pdfUrl) {
        return (
            <div className="flex items-center gap-2 text-sm text-foreground-muted" role="status">
                <Spinner size={16} /> Loading the draft…
            </div>
        )
    }
    const fileName = `${invoiceNumber ?? 'invoice'}.pdf`
    return (
        <div className="flex flex-col gap-3">
            <InlineDocument url={pdfUrl} title={title} file={file} />
            <div>
                <a
                    href={file.state === 'ready' ? file.url : pdfUrl}
                    download={fileName}
                    className="inline-flex items-center gap-2 rounded-3xl bg-default px-4 py-2 text-sm font-medium text-foreground hover:underline"
                >
                    <Download className="h-3.5 w-3.5" aria-hidden />
                    Download PDF
                </a>
            </div>
        </div>
    )
}
